import '@ionic/react/css/core.css'

import { IonModal } from '@ionic/react'
import {
  Avatar,
  Box,
  Button,
  ClickAwayListener,
  Divider,
  GlobalStyles,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react'
import {
  DeepMap,
  FieldError,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { UseFormTrigger } from 'react-hook-form/dist/types/form'
import {
  IconButton as UIconButton,
  Icons,
  InformationModal,
  Switch,
  ThumbnailUploadField,
} from 'utoniq-ui'

import {
  ItemType,
  Maybe,
  SkuSettingsInput,
  SkuUnit,
  SkuUnitInput,
} from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { useVerticalDnD } from '@/lib/dnd'

import { CreateItemProps, FileUploadInfo } from '../ItemCreateForm'
import { TextInputCell } from './Commons'

type SkuInputCellProps = {
  fields: { _id: string }[]
  hasIssuedNumber: boolean
  trigger: UseFormTrigger<CreateItemProps>
  errors?: DeepMap<Maybe<SkuSettingsInput> | undefined, FieldError>
  setValue: UseFormSetValue<CreateItemProps>
  watch: UseFormWatch<CreateItemProps>
  register: UseFormRegister<CreateItemProps>
  fileUploadInfo: FileUploadInfo
  onAppend: () => void
  onRemove: (index: number) => void
  onMove: (prevIndex: number, nextIndex: number) => void
  /** アプリによって、編集時にSKUごとの残数を表示するため、現在のSKUの在庫数を渡す。 */
  units?:
    | Pick<SkuUnit, 'purchasedNumber' | 'pendingPurchaseNumber'>[]
    | undefined
}

export const SkuInputCell: Cell<SkuInputCellProps> = ({
  fields,
  hasIssuedNumber,
  setValue,
  trigger,
  register,
  fileUploadInfo,
  watch,
  onAppend,
  onRemove,
  errors,
  onMove,
  units,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const [open, setOpen] = useState(false)
  const hasSkuWatch = watch('hasSku')

  return (
    <CellFrame sx={{ pb: 7 }}>
      <Stack direction="row" alignItems="center">
        <Stack flex={1}>
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5">{t('settingSKULabel')}</Typography>
              <Tooltip
                title={t('settingSKUDesc')}
                onClose={() => setOpen(false)}
                open={open}
                disableHoverListener
                disableTouchListener
                arrow
                slotProps={{
                  popper: {
                    disablePortal: true,
                  },
                }}
              >
                <UIconButton.Info onClick={() => setOpen(true)} />
              </Tooltip>
            </Box>
          </ClickAwayListener>
        </Stack>
        <Stack>
          <Switch
            sx={{
              '&& .Mui-disabled + .MuiSwitch-track': { bgcolor: 'grey.100' },
              '&& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
                bgcolor: 'primary.main',
              },
            }}
            checked={hasSkuWatch}
            onChange={() => {
              if (hasSkuWatch) {
                setValue('skuSettings.units', [] as never, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              } else {
                setValue(
                  'skuSettings.units',
                  [
                    {
                      unitName: '',
                      stock: undefined,
                      order: 1,
                      thumbnail: undefined,
                      price: null,
                    },
                  ] as never,
                  { shouldValidate: true, shouldDirty: true }
                )
              }
              setValue('hasSku', !hasSkuWatch, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
        </Stack>
      </Stack>
      {!!hasSkuWatch && (
        <TableContainer sx={{ py: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="normal"
                  sx={{ width: 'auto', pl: 0, pr: 2 }}
                >
                  <Typography variant="subtitle1">{t('skuValue')}</Typography>
                </TableCell>
                <TableCell padding="normal" sx={{ width: 78, pl: 0, pr: 2 }}>
                  <Typography variant="subtitle1">{t('thumbnail')}</Typography>
                </TableCell>
                <TableCell padding="normal" sx={{ width: 30, px: 0 }}>
                  <IconButton
                    sx={{
                      bgcolor: 'primary.main',
                      width: 20,
                      height: 20,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        opacity: 0.8,
                      },
                    }}
                    size="small"
                    disabled={fields.length > 19}
                    onClick={onAppend}
                  >
                    <Icons.Plus
                      sx={{ color: 'white', width: 10, height: 10 }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <SkuUnitCell
                  key={field._id}
                  index={index}
                  unitLength={fields.length}
                  fileUploadInfo={fileUploadInfo}
                  hasIssuedNumber={hasIssuedNumber}
                  register={register}
                  setValue={setValue}
                  onRemove={onRemove}
                  onMove={onMove}
                  watch={watch}
                  errors={errors?.units}
                  trigger={trigger}
                  unit={units?.[index]}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CellFrame>
  )
}

type SkuUnitCellProps = {
  index: number
  unitLength: number
  register: SkuInputCellProps['register']
  trigger: SkuInputCellProps['trigger']
  errors: DeepMap<Maybe<SkuUnitInput[]> | undefined, FieldError>
  setValue: SkuInputCellProps['setValue']
  onRemove: SkuInputCellProps['onRemove']
  hasIssuedNumber: SkuInputCellProps['hasIssuedNumber']
  fileUploadInfo: SkuInputCellProps['fileUploadInfo']
  watch: SkuInputCellProps['watch']
  onMove: (prevIndex: number, nextIndex: number) => void
  unit: Pick<SkuUnit, 'purchasedNumber' | 'pendingPurchaseNumber'> | undefined
}
const SkuUnitCell: Cell<SkuUnitCellProps> = ({
  index,
  unitLength,
  trigger,
  register,
  setValue,
  errors,
  onRemove,
  hasIssuedNumber,
  fileUploadInfo,
  watch,
  onMove,
  unit,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const [open, setOpen] = useState<boolean>(false)
  const unitNameWatch = watch(`skuSettings.units.${index}.unitName`)

  const { ref, drag, opacity } = useVerticalDnD({
    index,
    onMove,
    accept: 'SKU_ITEM',
  })

  useEffect(() => {
    if (!hasIssuedNumber) {
      setValue(`skuSettings.units.${index}.stock`, '' as never, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [hasIssuedNumber])

  const handleTriggerUnitStock = useCallback(async () => {
    await Promise.all(
      [...Array(unitLength).keys()].map(idx =>
        trigger(`skuSettings.units.${idx}.stock`)
      )
    )
  }, [trigger, unitLength])

  return (
    <TableRow ref={ref} sx={{ opacity }}>
      <TableCell
        padding="normal"
        sx={{ width: 'auto', borderBottom: 'none', pl: 0, pr: 2, pb: 0 }}
      >
        <Stack>
          <TextInputCell
            type="text"
            titleProps={{ sx: { display: 'none' } }}
            textFieldProps={{
              sx: { m: 0 },
              placeholder: t('unitNamePlaceholder'),
            }}
            {...register(`skuSettings.units.${index}.unitName`)}
            error={errors?.[index]?.unitName}
          />
          <TextInputCell
            type="number"
            titleProps={{ sx: { display: 'none' } }}
            textFieldProps={{
              sx: { m: 0 },
              placeholder: hasIssuedNumber
                ? t('stockPlaceholder')
                : t('stockNoLimitPlaceholder'),
            }}
            {...register(`skuSettings.units.${index}.stock`)}
            onChange={async event => {
              setValue(
                `skuSettings.units.${index}.stock`,
                event.target.value as never,
                { shouldValidate: true, shouldDirty: true }
              )
              await handleTriggerUnitStock()
            }}
            disabled={!hasIssuedNumber}
            error={errors?.[index]?.stock}
          />
          {unit && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 6,
                ml: 1,
              }}
            >
              <Typography variant="subtitle2">
                {t('remainingNumber')}
              </Typography>
              <Typography variant="subtitle2">
                {watch(`skuSettings.units.${index}.stock`) -
                  (unit?.purchasedNumber || 0) -
                  (unit?.pendingPurchaseNumber || 0)}
              </Typography>
            </Box>
          )}
        </Stack>
      </TableCell>
      <TableCell
        padding="normal"
        sx={{ width: 78, borderBottom: 'none', pl: 0, pr: 2, pb: 0 }}
      >
        <SkuThumbnail
          index={index}
          fileUploadInfo={fileUploadInfo}
          setValue={setValue}
          watch={watch}
        />
      </TableCell>
      <TableCell
        padding="normal"
        sx={{ width: 30, borderBottom: 'none', px: 0, pb: 0 }}
      >
        <Stack>
          <IconButton size="small" ref={drag} disableRipple>
            <Icons.Sort />
          </IconButton>
          <IconButton size="small" onClick={() => setOpen(true)}>
            <Icons.Trash />
          </IconButton>
          <InformationModal
            open={open}
            title={t('removeUnitTitle', { unitName: unitNameWatch || 'SKU' })}
            content={t('removeUnitDesc')}
            onClose={() => setOpen(false)}
            cancelText={t('close')}
            buttons={
              <Button
                size="large"
                color="primary"
                sx={{ minWidth: 212 }}
                onClick={async () => {
                  onRemove(index)
                  await handleTriggerUnitStock()
                }}
              >
                {t('remove')}
              </Button>
            }
          />
        </Stack>
      </TableCell>
    </TableRow>
  )
}

type SkuThumbnailProps = {
  index: number
  setValue: SkuInputCellProps['setValue']
  watch: SkuInputCellProps['watch']
  fileUploadInfo: SkuInputCellProps['fileUploadInfo']
}
const SkuThumbnail: FC<SkuThumbnailProps> = ({
  index,
  watch,
  setValue,
  fileUploadInfo,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const [open, setOpen] = useState<boolean>(false)
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('')
  const thumbnailWatch = watch(`skuSettings.units.${index}.thumbnail`)
  const thumbnailTypeWatch = watch('thumbnailType')

  useEffect(() => {
    setThumbnailUrl(thumbnailWatch)
  }, [])

  return (
    <CellFrame>
      <ThumbnailUploadField
        t={t}
        shape={thumbnailTypeWatch as 'square' | 'rectangle'}
        thumbnailProps={{
          src: thumbnailUrl,
          onClick() {
            setOpen(true)
          },
        }}
        fileUploadInfo={{
          type: 'image',
          progress: fileUploadInfo.progress,
          onDrop() {
            return fileUploadInfo.onDrop(ItemType.Image).then(resource => {
              if (!resource) return
              setThumbnailUrl(resource.imageUrl ?? '')
              setValue(
                `skuSettings.units.${index}.thumbnail`,
                resource.imageUploadPath as never,
                { shouldValidate: true, shouldDirty: true }
              )
              return resource
            })
          },
        }}
      />
      <FullHeightModal open={open} onClose={() => setOpen(false)}>
        <Stack>
          <Typography variant="h6" align="center" sx={{ pt: 5, pb: 3 }}>
            {t('thumbnailImage')}
          </Typography>
          <Stack alignItems="center" sx={{ mt: 2, mb: 7 }}>
            <Box sx={{ position: 'relative', width: 'fit-content' }}>
              <IconButton
                size="small"
                onClick={() => {
                  setOpen(false)
                  setThumbnailUrl('')
                  setValue(
                    `skuSettings.units.${index}.thumbnail`,
                    null as never,
                    { shouldValidate: true, shouldDirty: true }
                  )
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                  bgcolor: 'white',
                  '&:hover': {
                    bgcolor: 'white',
                    opacity: 0.8,
                  },
                }}
              >
                <Icons.Close sx={{ color: 'grey.700' }} />
              </IconButton>
              <Avatar
                variant="square"
                sx={{
                  width: 250,
                  height: thumbnailTypeWatch === 'square' ? 250 : 400,
                }}
                src={thumbnailUrl}
              />
            </Box>
          </Stack>
          <Divider />
          <Stack alignItems="center" sx={{ py: 4 }}>
            <Button variant="text" onClick={() => setOpen(false)}>
              {t('close')}
            </Button>
          </Stack>
        </Stack>
      </FullHeightModal>
    </CellFrame>
  )
}

type FullHeightModalProps = {
  open: boolean
  onClose: () => void
}
const FullHeightModal: FC<PropsWithChildren<FullHeightModalProps>> = ({
  open,
  onClose,
  children,
}) => {
  /** IonModalの初期化時にページがスクロールできるようにするための処理 */
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'scroll'
  }, [open])

  return (
    <CellFrame>
      <GlobalStyles
        styles={{
          'ion-modal#fullHeightModal': {
            '--height': 'auto',
          },
        }}
      />
      <IonModal
        isOpen={open}
        onDidDismiss={onClose}
        initialBreakpoint={1}
        breakpoints={[0, 1]}
        id="fullHeightModal"
      >
        {children}
      </IonModal>
    </CellFrame>
  )
}
